import React from "react"
import moment from "moment"
import { BiFilter } from "react-icons/bi"
import styles from "./leadFilters.module.css"

const collectionOperatorLabels = {
    any: "qualquer um",
    all: "todos",
    exact: "exatamente",
    none: "nenhum",
    is_empty: "nenhum cadastrado",
    is_not_empty: "algum cadastrado",
}

const datePresetLabels = {
    all: "Todo o período",
    today: "Hoje",
    yesterday: "Ontem",
    last7Days: "Últimos 7 dias",
    last14Days: "Últimos 14 dias",
    last30Days: "Últimos 30 dias",
    thisWeek: "Esta semana",
    lastWeek: "Semana passada",
    thisMonth: "Este mês",
    lastMonth: "Mês passado",
}

const utmFieldLabels = {
    source: "UTM Source",
    medium: "UTM Medium",
    campaign: "UTM Campaign",
    content: "UTM Content",
    term: "UTM Term",
    url: "UTM URL",
}

const utmOperatorLabels = {
    equals: "igual a",
    contains: "contém",
    starts_with: "começa com",
    is_set: "está preenchido",
    is_not_set: "não está preenchido",
}

const interactionStatusLabels = {
    no_activity: "Sem atividade registrada",
    no_contact_attempt: "Sem primeiro contato",
    awaiting_response: "Contato realizado, aguardando resposta",
    contacted: "Contato efetivo",
}

const getOptionLabel = (value, options = []) => options.find(option => String(option.id) === String(value))?.value || value

const summarizeSelection = (values, options = []) => {
    if (!Array.isArray(values) || !values.length) return ""
    if (values.length === 1) return getOptionLabel(values[0], options)
    return `${values.length} selecionados`
}

const addSelectionChip = (chips, key, label, values, options) => {
    const summary = summarizeSelection(values, options)
    if (summary) chips.push({ key, label, value: summary })
}

const addCollectionChip = (chips, key, label, values, operator, options) => {
    const normalizedOperator = operator || "any"
    if (["is_empty", "is_not_empty"].includes(normalizedOperator)) {
        chips.push({ key, label, value: collectionOperatorLabels[normalizedOperator] })
        return
    }

    const summary = summarizeSelection(values, options)
    if (summary) chips.push({ key, label, value: `${collectionOperatorLabels[normalizedOperator] || normalizedOperator}: ${summary}` })
}

const buildLeadFilterChips = ({ filters = {}, options = {}, dashboard = false }) => {
    const chips = []

    if (filters.search) chips.push({ key: "search", label: "Busca", value: filters.search })
    if (filters.datePreset || filters.dateStart || filters.dateEnd) {
        const start = filters.dateStart ? moment(filters.dateStart).format("DD/MM/YYYY") : "início"
        const end = filters.dateEnd ? moment(filters.dateEnd).format("DD/MM/YYYY") : "hoje"
        const typeDate = getOptionLabel(filters.typeDate, options.typeDate)
        const period = datePresetLabels[filters.datePreset] || `${start} – ${end}`
        chips.push({ key: "period", label: "Período", value: `${period}${!dashboard && typeDate ? ` · ${typeDate}` : ""}` })
    }

    if (filters.funnel_id) addSelectionChip(chips, "funnel", "Funil", [filters.funnel_id], options.funnels)
    if (dashboard) {
        addSelectionChip(chips, "funnels", "Funis", filters.funnels, options.funnels)
    }
    if (dashboard || filters.funnel_id) {
        addSelectionChip(chips, "stages", "Etapas", filters.stages, options.stages)
    }
    addCollectionChip(chips, "products", "Produtos", filters.products, filters.productsOperator, options.products)
    addSelectionChip(chips, "origins", "Origens", filters.origins, options.origins)
    addSelectionChip(chips, "campaigns", "Campanhas", filters.campaigns, options.campaigns)
    addCollectionChip(chips, "flags", "Etiquetas", filters.flags, filters.flagsOperator, options.flags)
    addSelectionChip(chips, "status", "Status", filters.status, options.status)
    if (filters.interactionStatus) chips.push({ key: "interactionStatus", label: "Interação", value: interactionStatusLabels[filters.interactionStatus] || filters.interactionStatus })
    addSelectionChip(chips, "teams", "Equipes", filters.teams, options.teams)
    addCollectionChip(chips, "users", "Responsáveis", filters.users, filters.usersOperator, options.users)

    if (filters.additionalFieldFilters?.length) {
        chips.push({ key: "additional", label: "Campos adicionais", value: `${filters.additionalFieldFilters.length} ${filters.additionalFieldFilters.length === 1 ? "condição" : "condições"}` })
    }
    filters.utmFilters?.forEach((filter, index) => {
        const operator = utmOperatorLabels[filter.operator] || filter.operator
        const value = ["is_set", "is_not_set"].includes(filter.operator) ? operator : `${operator}: ${filter.value || "-"}`
        chips.push({ key: `utm-${index}`, label: utmFieldLabels[filter.field] || "UTM", value })
    })
    addSelectionChip(chips, "metaAdsAdsName", "Anúncios", filters.metaAdsAdsName, options.metaAdsAdsName)
    addSelectionChip(chips, "metaAdsAdsetsName", "Conjuntos", filters.metaAdsAdsetsName, options.metaAdsAdsetsName)
    addSelectionChip(chips, "metaAdsCampaignsName", "Campanhas Meta", filters.metaAdsCampaignsName, options.metaAdsCampaignsName)
    addSelectionChip(chips, "metaAdsFormsName", "Formulários", filters.metaAdsFormsName, options.metaAdsFormsName)
    addSelectionChip(chips, "metaAdsPagesName", "Páginas", filters.metaAdsPagesName, options.metaAdsPagesName)

    return chips
}

const ActiveFilterChips = ({ items = [], onOpen }) => {
    if (!items.length) return null

    return (
        <div className={styles.activeFilters}>
            <button type="button" className={styles.activeFiltersTitle} onClick={onOpen} title={onOpen ? "Editar filtros" : undefined} disabled={!onOpen}>
                <BiFilter />
                Filtros ativos
            </button>
            <div className={styles.activeFilterList}>
                {items.map(item => (
                    <button type="button" className={styles.activeFilterChip} key={item.key} onClick={onOpen} title={`${item.label}: ${item.value}`} disabled={!onOpen}>
                        <strong>{item.label}</strong>
                        <span>{item.value}</span>
                    </button>
                ))}
            </div>
        </div>
    )
}

export { buildLeadFilterChips }
export default ActiveFilterChips
